import { ReactElement } from 'react';

import { ICreateUploadQueueItemParamRequest } from '../models/interfaces/ICreateUploadQueueItemParamRequest';
import { ICreateUploadQueueItemRequest } from '../models/interfaces/ICreateUploadQueueItemRequest';
import { IWizardFile } from '../models/interfaces/IWizardFile';

export interface IItemActionsProps {
    entityKey: string;
    queueToken: string;
    isExpanded: boolean;
    projectToken?: string;
    onAssociateAttributes: () => void;
    onShowMetadata: () => void;
    isLoadingMetadata: boolean;
    onTraceHeaderInspect: () => void;
}

export type FileGroup = {
    parent: IWizardFile;
    children: IWizardFile[];
};

export abstract class QueueTypeHandler {
    abstract readonly extensionsAccepted: readonly string[];

    readonly mainExtensionsToGroup: string | null = null;

    validateFileExtension(fileName: string): boolean {
        if (this.extensionsAccepted.length === 0) {
            return true;
        }
        const lower = fileName.toLowerCase();
        return this.extensionsAccepted.some((ext) => lower.endsWith(ext));
    }

    groupFiles(files: IWizardFile[]): FileGroup[] {
        if (this.mainExtensionsToGroup === null) {
            return files.map((file) => ({ parent: file, children: [] }));
        }

        const ext = this.mainExtensionsToGroup;
        const mainFiles = files.filter((f) => f.name.toLowerCase().endsWith(ext));

        return mainFiles.map((mainFile) => {
            const baseName = mainFile.name.toLowerCase().slice(0, -ext.length);
            const children = files.filter((f) => {
                if (f === mainFile) {
                    return false;
                }
                const lower = f.name.toLowerCase();
                const dotIndex = lower.lastIndexOf('.');
                const fileBase = dotIndex !== -1 ? lower.slice(0, dotIndex) : lower;
                return fileBase === baseName;
            });
            return { parent: mainFile, children };
        });
    }

    buildItems(files: IWizardFile[], params: ICreateUploadQueueItemParamRequest[]): ICreateUploadQueueItemRequest[] {
        return this.groupFiles(files).map(({ parent, children }) => {
            const hasChildren = children.length > 0;
            return {
                Priority: 0,
                SizeBytes: parent.size ?? null,
                Path: hasChildren ? '' : (parent.path ?? ''),
                Paths: hasChildren ? [parent, ...children].map((f) => f.path ?? '') : [],
                Params: params,
            };
        });
    }

    abstract renderParams(onParamsChange?: (params: Record<string, unknown>) => void, initialValues?: Record<string, string>): ReactElement;

    renderItemActions(_props: IItemActionsProps): ReactElement | null {
        return null;
    }
}
